import React, { forwardRef, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import mstyles from '../modules.module.css'
import styles from './webview-grid.module.scss'
import { getContentPath } from 'system/AssetManager'
import { ReactComponent as CloseButton } from 'assets/icons/close.svg'
import Globals from 'system/Globals'

const CHROME_HEIGHT = 34

const WebViewGridModule = forwardRef((props, ref) => {
    const [activeIndex, setActiveIndex] = useState(null)
    const [currentUrl, setCurrentUrl] = useState('')
    const [bvFullscreen, setBvFullscreen] = useState(false)
    const [toastVisible, setToastVisible] = useState(false)
    const [currentZoom, setCurrentZoom] = useState(1)
    const [format, setFormat] = useState(null)
    const [user] = useState(() => {
        try { return JSON.parse(localStorage.getItem('user')) } catch { return null }
    })
    const refBrowserArea = useRef()

    // Stable BrowserView ID — keyed on the module's PK, not the tile index.
    // browserModal.show() requires a non-null id.
    const bvId = props.model.pk

    useEffect(() => {
        props.preAnimateWindowIn()
        props.animateWindowIn()
        if (props.socket) {
            props.socket.emit('handshake', [props.socketPrefs.room, props.socket.id, 'select-module?module=' + props.index])
        }
        if (props.model.formats) {
            setFormat(Globals.instance().getFormat(props.model.formats))
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Controller / server-event listeners (isReceive / serverApp mode)
    useEffect(() => {
        if (!props.socketPrefs) return
        if (!(Globals.instance().serverApp || props.socketPrefs.mode === 'isReceive')) return

        const handler = (evt) => {
            const detail = evt.detail
            if (!detail) return
            if (detail.event === 'module-webview-grid-select-view') {
                setActiveIndex(parseInt(detail.command))
                if (props.socket) {
                    props.socket.emit('handshake', [props.socketPrefs.room, props.socket.id, 'module-webview-grid-select-view?command=' + detail.command])
                }
            }
            if (detail.event === 'module-webview-grid-command' && detail.command === 'close') {
                setActiveIndex(null)
                if (props.socket) {
                    props.socket.emit('handshake', [props.socketPrefs.room, props.socket.id, 'module-webview-grid-command?command=close'])
                }
            }
        }
        window.addEventListener('server-event', handler)
        return () => window.removeEventListener('server-event', handler)
    }, [props.socketPrefs]) // eslint-disable-line react-hooks/exhaustive-deps

    // Desktop → renderer: URL updates and fullscreen state changes
    useEffect(() => {
        const handler = (e) => {
            if (!e.detail || e.detail.id !== bvId) return
            if (e.detail.event === 'webview-url-updated') setCurrentUrl(e.detail.url)
            if (e.detail.event === 'webview-bv-fullscreen') setBvFullscreen(!!e.detail.state)
        }
        window.addEventListener('window-event', handler)
        return () => window.removeEventListener('window-event', handler)
    }, [bvId])

    // Fullscreen toast — brief "Press Esc" hint on entry
    useEffect(() => {
        if (!bvFullscreen) { setToastVisible(false); return }
        setToastVisible(true)
        const t = setTimeout(() => setToastVisible(false), 1500)
        return () => clearTimeout(t)
    }, [bvFullscreen])

    // BrowserView IPC — runs whenever detail opens or closes
    useEffect(() => {
        if (!Globals.instance().electron) return
        if (activeIndex === null) return

        const item = props.model.webviews[activeIndex]
        if (!item || !item.webview_url) return

        const url = item.webview_url.startsWith('http://') || item.webview_url.startsWith('https://')
            ? item.webview_url : 'https://' + item.webview_url

        // Seed the URL bar immediately; did-navigate will update it after load
        setCurrentUrl(url)
        setBvFullscreen(false)

        const sendBounds = () => {
            if (!refBrowserArea.current) return
            const rect = refBrowserArea.current.getBoundingClientRect()
            const zoom = Math.round((window.innerWidth / 1920) * 1000) / 1000
            setCurrentZoom(zoom)
            window.api.send('toApp', {
                event: 'webview-show',
                id: bvId,
                url,
                zoom,
                bounds: {
                    x: Math.round(rect.left),
                    y: Math.round(rect.top),
                    width: Math.round(rect.width),
                    height: Math.round(rect.height)
                }
            })
        }

        sendBounds()

        let resizeTimer = null
        const onResize = () => {
            clearTimeout(resizeTimer)
            resizeTimer = setTimeout(sendBounds, 300)
        }
        window.addEventListener('resize', onResize)

        return () => {
            clearTimeout(resizeTimer)
            window.removeEventListener('resize', onResize)
            window.api.send('toApp', { event: 'webview-hide', id: bvId })
        }
    }, [activeIndex]) // eslint-disable-line react-hooks/exhaustive-deps

    function openDetail(index) {
        setActiveIndex(index)
        if (props.socketPrefs && props.socketPrefs.mode === 'isBroadcast') {
            props.socket.emit('cast', [props.socketPrefs.room, 'module-webview-grid-select-view?command=' + index])
        }
    }

    function closeDetail() {
        setActiveIndex(null)
        setBvFullscreen(false)
        if (props.socketPrefs && props.socketPrefs.mode === 'isBroadcast') {
            props.socket.emit('cast', [props.socketPrefs.room, 'module-webview-grid-command?command=close'])
        }
        // webview-hide is sent by the BrowserView useEffect cleanup above
    }

    const sendNav = (event) => {
        if (Globals.instance().electron) window.api.send('toApp', { event, id: bvId })
    }

    const activeItem = activeIndex !== null ? (props.model.webviews || [])[activeIndex] : null

    const hasInset = !!(format && user && user.letterbox && format.inset_width != null)
    const detailContainerStyle = hasInset ? {
        position: 'absolute',
        background: 'transparent',
        width: format.inset_width * 100 + 'vw',
        maxHeight: format.inset_height * 100 + 'vh',
        left: format.inset_x * 100 + 'vw',
        top: format.inset_y * 100 + 'vh',
        aspectRatio: (format.inset_aspect_ratio ? format.inset_aspect_ratio.toString() : format.aspect_ratio),
        borderRadius: '8px',
        overflow: 'hidden',
    } : {
        position: 'absolute',
        width: '96vw',
        height: '96vh',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: props.model.backgroundColor || '#000',
        borderRadius: '8px',
        overflow: 'hidden',
    }

    const chromeBarContent = (
        <>
            <svg onClick={() => sendNav('webview-back')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            <svg onClick={() => sendNav('webview-forward')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
            </svg>
            <svg onClick={() => sendNav('webview-reload')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            <svg onClick={() => sendNav('webview-home')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            <div style={{flex:1, background:'#c8c8c8', borderRadius:'4px', height:'18px', display:'flex', alignItems:'center', padding:'0 6px', overflow:'hidden'}}>
                <span style={{fontSize:'9px', color:'#555', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1.4}}>
                    {currentUrl}
                </span>
            </div>
            <svg onClick={() => sendNav('webview-fullscreen')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                {bvFullscreen
                    ? <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                    : <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                }
            </svg>
        </>
    )

    const chromeBar = (
        <div style={{
            height: CHROME_HEIGHT + 'px',
            minHeight: CHROME_HEIGHT + 'px',
            background: '#c8c8c8',
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            gap: '5px',
            flexShrink: 0,
            userSelect: 'none'
        }}>
            {chromeBarContent}
        </div>
    )

    return (
        <div
            className={`fullscreen ${styles.container}`}
            style={{ background: activeIndex !== null ? 'transparent' : (props.model.backgroundColor || '#6a6a6a') }}
            ref={ref}
        >
            {props.model.background_image && activeIndex === null && (
                <img src={getContentPath(props.model.background_image)} alt="" className={styles.backgroundImage} />
            )}

            {/* Landing grid */}
            {activeIndex === null && (
                <div className={`fullscreen ${styles.scrollContainer} hide-scroller`}>
                    <div className={styles.landingContainer}>
                        <div className={styles.letterbox}>
                            <div
                                className={styles.heading}
                                style={Globals.instance().getThemeStyle(['module-landing-primary'], props.theme)}
                            >
                                {props.model.title}
                            </div>
                            <div className={`fullscreen ${styles.itemsContainer}`}>
                                {(props.model.webviews || []).map((item, i) => (
                                    <div
                                        key={`wv-tile-${i}`}
                                        className={styles.thumbnail}
                                        onClick={() => openDetail(i)}
                                    >
                                        {item.image
                                            ? <img src={getContentPath(item.image)} alt="" />
                                            : <div className={styles.thumbnailPlaceholder} />
                                        }
                                        <div
                                            className={styles.caption}
                                            style={Globals.instance().getThemeStyle(['module-landing-primary'], props.theme)}
                                        >
                                            {item.title || item.caption}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail view */}
            {activeIndex !== null && activeItem && (
                <>
                {format && format.file && (
                    <img src={getContentPath(format.file)} alt="" className={styles.backgroundImage} />
                )}
                <div
                    className={styles.detailContainer}
                    style={detailContainerStyle}
                >
                    {chromeBar}
                    <div ref={refBrowserArea} className={styles.detailBrowserArea}>
                        {!Globals.instance().electron && activeItem.webview_url && (
                            <iframe
                                src={activeItem.webview_url}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                title={activeItem.title || 'webview'}
                                allow="fullscreen"
                            />
                        )}
                    </div>
                </div>
                </>
            )}

            {/* Fullscreen chrome portal — BrowserView covers the whole window;
                portal the chrome bar above it so nav controls remain accessible */}
            {bvFullscreen && ReactDOM.createPortal(
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0,
                    height: Math.round(CHROME_HEIGHT * currentZoom) + 'px',
                    background: '#c8c8c8',
                    display: 'flex', alignItems: 'center',
                    padding: '0 8px',
                    gap: '5px',
                    zIndex: 9999,
                    userSelect: 'none',
                    boxSizing: 'border-box',
                }}>
                    {chromeBarContent}
                    <div style={{
                        background: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        borderRadius: 999,
                        padding: '2px 10px',
                        fontSize: 10,
                        whiteSpace: 'nowrap',
                        opacity: toastVisible ? 1 : 0,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none',
                        marginLeft: 4,
                        flexShrink: 0,
                    }}>
                        Press Esc to exit
                    </div>
                </div>,
                document.body
            )}

            {/* Close: detail → grid, grid → close module */}
            <div className={`${mstyles.closeBtnContainer} ${styles.closeBtnContainer}`}>
                <div
                    className="round-btn"
                    onClick={() => activeIndex !== null ? closeDetail() : props.closeModule()}
                >
                    <CloseButton />
                </div>
            </div>
        </div>
    )
})

export default WebViewGridModule
